"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
function useScrollThreshold(threshold = 20) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > threshold);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [threshold]);
  return scrolled;
}
function useSmoothScroll() {
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
}
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="h-4 w-64 animate-pulse rounded bg-white/5" />;
  }
  const menuOrder = ["About Us", "Thesis", "Partnerships", "Leadership", "Contact"];
  const sortedMenuItems = [...menuItems].sort((a, b) => {
    const getIndex = (label: string) => {
      const lowerLabel = label.toLowerCase();
      const index = menuOrder.findIndex(orderItem => lowerLabel.includes(orderItem.toLowerCase()));
      return index === -1 ? 999 : index;
    };
    return getIndex(a.label) - getIndex(b.label);
  });
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6">
        {sortedMenuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative font-sans text-[14px] font-medium uppercase tracking-[0.1em] text-foreground/70 transition-colors hover:text-primary">
                {item.label}
                <span className="absolute -bottom-2 left-1/2 h-0.5 w-1 -translate-x-1/2 rounded-full bg-primary opacity-0 shadow-[0_0_8px_rgba(0,255,255,0.8)] transition-all duration-300 group-hover:opacity-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuContent({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 pt-10">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-32 animate-pulse rounded bg-white/5" />)}
      </div>;
  }
  const menuOrder = ["About Us", "Thesis", "Partnerships", "Leadership", "Contact"];
  const sortedMenuItems = [...menuItems].sort((a, b) => {
    const getIndex = (label: string) => {
      const lowerLabel = label.toLowerCase();
      const index = menuOrder.findIndex(orderItem => lowerLabel.includes(orderItem.toLowerCase()));
      return index === -1 ? 999 : index;
    };
    return getIndex(a.label) - getIndex(b.label);
  });
  return <nav className="flex flex-col gap-6 pt-10">
      {sortedMenuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="font-sans text-2xl font-bold uppercase tracking-[0.1em] text-foreground hover:text-primary transition-colors" onClick={onClose}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const isScrolled = useScrollThreshold(20);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useSmoothScroll();
  const businessName = "N2N Capital";
  return <header id="header" className={`fixed top-0 left-0 z-50 w-full border-b transition-all duration-300 dark bg-[#172032] text-foreground ${isScrolled ? "bg-[#172032]/80 backdrop-blur-xl border-white/10 shadow-2xl" : "border-[#1A1A1A]"}`}>
      <div className="container mx-auto flex h-[88px] items-center justify-between px-4 md:px-6">
        
        <Link to="/" className="flex items-center gap-2 z-50">
          <img src="https://decent-kid.10web.cloud/wp-content/uploads/2026/03/N2N-e1777347198209.jpeg" alt={`${businessName} Logo`} className="h-19 w-auto object-contain" />
        </Link>

        <div className="hidden md:block">
          <MenuProvider menu_id="40">
            <DesktopMenu />
          </MenuProvider>
        </div>

        <div className="md:hidden">
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-foreground hover:bg-white/5 rounded-sm" aria-label="Toggle menu">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="top" className="h-full w-full border-none bg-[#050505]/95 backdrop-blur-2xl p-6 pt-24 dark">
              <SheetHeader className="sr-only">
                <SheetTitle>Navigation Menu</SheetTitle>
                <SheetDescription>Access site pages and resources</SheetDescription>
              </SheetHeader>
              <MenuProvider menu_id="40">
                <MobileMenuContent onClose={() => setIsMobileMenuOpen(false)} />
              </MenuProvider>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>;
}